import { CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/flexkit/Command";
import { useListProjectEnvironmentClassEntries, type PlainProject } from "@/queries/project-queries";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { formatError } from "@/utils/errors";
import { CommandLoading } from "cmdk";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { memo, useEffect } from "react";
import { Code, ConnectError } from "@connectrpc/connect";
import { useStore } from "jotai";
import { useIsUserAuthenticatedWithRunner } from "@/queries/runner-queries";
import { useEnvironmentClassEntries } from "@/hooks/use-environment-class-entries";
import { SCMAuthenticationModal } from "@/routes/environments/create/SCMAuthentication";
import { TrackLocations } from "@/hooks/use-segment";
import { useListContextUrls } from "@/hooks/use-context-url";
import IconPlusLarge from "central-icons/IconPlusLarge";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import type { ParseContextURLResponse } from "gitpod-next-api/gitpod/v1/runner_pb";
import type { SelectedContext } from "@/components/ai/prompt-input/NewPromptInput";
import { getProjectWithCustomInitializer } from "@/queries/environment-queries";
import { isSameRepository } from "@/components/ai/prompt-input/context-utils";
import type { OnSelectContextCallback } from "@/components/ai/prompt-input/context-selector-menu/ContextSelectorMenu";
import { errorAtom, searchQueryAtom, isParsingUrlAtom, pendingContextUrlAtom } from "@/components/ai/prompt-input/context-selector-menu/state";

/**
 * stores the pending context selections when a project is detected from the selected context url.
 * this is used to trigger the project environment class query. when the list of env classes for the project is queried, the onSelectContext callback is triggered, and this atom is cleared.
 */
const pendingProjectContextSelectionAtom = atom<{
    contexts: SelectedContext[];
    preferredProject: PlainProject;
    associatedProjects: PlainProject[];
} | null>(null);

type UrlSelectorMenuProps = {
    onSelectContext: OnSelectContextCallback;
};

const ErrorMessage = memo(({ error }: { error: unknown }) => {
    return (
        <div className="m-1 rounded bg-content-destructive/20 px-3 py-1 text-base text-content-destructive">
            {formatError(error)}
        </div>
    );
});
ErrorMessage.displayName = "ErrorMessage";

const UrlSelectorMenu = memo(({ onSelectContext }: UrlSelectorMenuProps) => {
    const api = useGitpodAPI();
    const store = useStore();
    const { contextUrls, isPending: isLoadingContextUrlList } = useListContextUrls();
    const [isParsingUrl, setIsParsingUrl] = useAtom(isParsingUrlAtom);
    const [error, setError] = useAtom(errorAtom);
    const setPendingContextUrl = useSetAtom(pendingContextUrlAtom);
    const setPendingProjectContextSelection = useSetAtom(pendingProjectContextSelectionAtom);

    const selectUrlAsContext = (parsedContext: ParseContextURLResponse, associatedProjects: PlainProject[]) => {
        let newContexts: SelectedContext[] = [];
        let newContext: SelectedContext | null = null;

        // Create the new context based on the parsed response
        if (parsedContext.pr && parsedContext.git) {
            newContext = {
                kind: "pr",
                url: parsedContext.originalContextUrl,
                title: parsedContext.pr.title,
                repoName: parsedContext.git.repo,
                ownerName: parsedContext.git.owner,
                fromBranchName: parsedContext.pr.fromBranch,
                toBranchName: parsedContext.pr.toBranch,
                scmId: parsedContext.scmId,
            };
        } else if (parsedContext.issue && parsedContext.git) {
            newContext = {
                kind: "issue",
                url: parsedContext.originalContextUrl,
                title: parsedContext.issue.title,
                branchName: parsedContext.git.branch,
                repoName: parsedContext.git.repo,
                ownerName: parsedContext.git.owner,
                scmId: parsedContext.scmId,
            };
        } else if (parsedContext.git?.tag) {
            newContext = {
                kind: "git-tag",
                url: parsedContext.originalContextUrl,
                tagName: parsedContext.git.tag,
                repoName: parsedContext.git.repo,
                ownerName: parsedContext.git.owner,
                scmId: parsedContext.scmId,
            };
        } else if (parsedContext.git?.commit) {
            newContext = {
                kind: "git-commit",
                url: parsedContext.originalContextUrl,
                commitHash: parsedContext.git.commit,
                repoName: parsedContext.git.repo,
                ownerName: parsedContext.git.owner,
                scmId: parsedContext.scmId,
            };
        } else if (parsedContext.git?.branch) {
            newContext = {
                kind: "repo",
                url: parsedContext.originalContextUrl,
                branchName: parsedContext.git.branch,
                repoName: parsedContext.git.repo,
                ownerName: parsedContext.git.owner,
                scmId: parsedContext.scmId,
            };
        }

        // If we have a new context, replace any existing context for the same repository
        if (newContext) {
            // Remove any existing contexts for the same repository
            newContexts = newContexts.filter(
                (context) =>
                    !isSameRepository(context, {
                        ownerName: newContext.ownerName,
                        repoName: newContext.repoName,
                        url: newContext.url,
                    }),
            );
            // Add the new context
            newContexts.push(newContext);
        }

        if (associatedProjects && associatedProjects.length > 0) {
            // Sort projects by user count (totalSubjects) in descending order to prioritize most popular projects
            const sortedProjects = [...associatedProjects].sort((a, b) => {
                const aUserCount = a.usedBy?.totalSubjects ?? 0;
                const bUserCount = b.usedBy?.totalSubjects ?? 0;
                return bUserCount - aUserCount;
            });

            // For PR contexts, modify the project to use the PR's head branch
            let projectToAdd = sortedProjects[0];
            if (newContext && newContext.kind !== "repo") {
                let ref: string;
                let refType: "branch" | "commit" | "tag";
                switch (newContext.kind) {
                    case "pr":
                        ref = newContext.fromBranchName;
                        refType = "branch";
                        break;
                    case "issue":
                        ref = newContext.branchName;
                        refType = "branch";
                        break;
                    case "git-tag":
                        ref = newContext.tagName;
                        refType = "tag";
                        break;
                    case "git-commit":
                        ref = newContext.commitHash;
                        refType = "commit";
                }
                projectToAdd = getProjectWithCustomInitializer(sortedProjects[0], {
                    ref,
                    refType,
                });
            }

            setPendingProjectContextSelection({
                contexts: newContexts,
                preferredProject: projectToAdd,
                associatedProjects: sortedProjects,
            });
        } else {
            onSelectContext(newContexts, []);
        }
    };

    const validateAndAddUrl = async (maybeUrl: string, runnerId?: string) => {
        // Extract SCM host for error messages
        let scmHost = "";
        try {
            scmHost = new URL(maybeUrl).host;
        } catch {
            setError(new Error("Invalid repository URL"));
            return;
        }

        try {
            setIsParsingUrl(true);

            const parsed = await api.runnerService.parseContextURL({ contextUrl: maybeUrl, runnerId });
            if (parsed.projectIds?.length > 0) {
                const projects = await Promise.all(
                    parsed.projectIds.map((id) =>
                        api.projectService.getProject({ projectId: id }).then(({ project }) => project),
                    ),
                );
                selectUrlAsContext(
                    parsed,
                    projects.filter((it) => it !== undefined),
                );
            } else {
                selectUrlAsContext(parsed, []);
            }
        } catch (error) {
            console.error("error parsing context URL in UrlSelectorMenu", error);

            // Handle authentication errors
            if (error instanceof ConnectError) {
                switch (error.code) {
                    case Code.FailedPrecondition:
                        handleFailedPreconditionError(error, maybeUrl);
                        return;
                    case Code.NotFound:
                        // No suitable runners found
                        setError(
                            new Error(
                                scmHost
                                    ? `No runners are available to access repositories on ${scmHost}`
                                    : "No runners are available to access this repository",
                            ),
                        );
                        return;
                    case Code.Unauthenticated:
                        // User not authenticated with the system
                        setError(new Error("Authentication required. Please log in to access this feature"));
                        return;
                    default:
                        setError(error);
                        return;
                }
            } else {
                setError(error);
            }
        } finally {
            setIsParsingUrl(false);
        }
    };

    /**
     * Handles failed precondition errors thrown by parse context url rpc call
     */
    const handleFailedPreconditionError = (_error: ConnectError, maybeUrl: string) => {
        setPendingContextUrl(maybeUrl);
    };

    const addQueryAsContext = async () => {
        const query = store.get(searchQueryAtom);
        await validateAndAddUrl(query);
    };

    const onAuthenticationSuccessful = async (runnerId: string) => {
        const contextUrl = store.get(pendingContextUrlAtom);
        if (contextUrl) {
            setPendingContextUrl("");
            await validateAndAddUrl(contextUrl, runnerId);
        }
    };

    const closeRunnerAuthenticationModal = () => {
        setPendingContextUrl("");
    };

    if (error) {
        return <ErrorMessage error={error} />;
    }

    return (
        <ProjectEnvironmentClassQueryContainer onSelectContext={onSelectContext}>
            <CommandList className="max-h-40 overflow-auto">
                {isLoadingContextUrlList ? (
                    <CommandLoading className="px-3 py-1 opacity-80">Loading…</CommandLoading>
                ) : null}
                <SearchQueryContextItem onSelect={addQueryAsContext} />
                <CommandGroup heading="Recent">
                    <CommandEmpty>No recent URLs</CommandEmpty>
                    {contextUrls?.map((url) => (
                        <CommandItem
                            key={url}
                            disabled={isParsingUrl}
                            onSelect={validateAndAddUrl}
                            className="cursor-pointer"
                        >
                            {url}
                        </CommandItem>
                    ))}
                </CommandGroup>
            </CommandList>
            <RunnerAuthenticationModal
                onClose={closeRunnerAuthenticationModal}
                onAuthenticationSuccess={onAuthenticationSuccessful}
            />
        </ProjectEnvironmentClassQueryContainer>
    );
});
UrlSelectorMenu.displayName = "UrlSelectorMenu";

/**
 * Renders the current search query as a selectable CommandItem in its own group.
 * This ensures the pasted/typed URL is always visible and selectable via cmdk's
 * native focus management, even when it fuzzy-matches a recent URL.
 */
const SearchQueryContextItem = memo(({ onSelect }: { onSelect: () => void }) => {
    const query = useAtomValue(searchQueryAtom);
    const isParsingUrl = useAtomValue(isParsingUrlAtom);
    const pendingContextUrl = useAtomValue(pendingContextUrlAtom);

    if (!query) {
        return null;
    }

    const isAdding = isParsingUrl || !!pendingContextUrl;

    return (
        <CommandGroup>
            <CommandItem
                // Prefix avoids cmdk deduplication with recent URL items that
                // share the same text content.
                value={`__add__${query}`}
                disabled={isAdding}
                onSelect={onSelect}
                className="cursor-pointer"
                data-tracking-id="add-context-item"
                // Ensure this item always matches the current search query.
                keywords={[query]}
            >
                {isAdding ? (
                    <IconSpinner size="sm" className="size-4 animate-spin" />
                ) : (
                    <IconPlusLarge className="size-4 shrink-0" />
                )}
                <span>{isAdding ? "Adding…" : `Add "${query}"`}</span>
            </CommandItem>
        </CommandGroup>
    );
});
SearchQueryContextItem.displayName = "SearchQueryContextItem";

const ProjectEnvironmentClassQueryContainer = memo(
    ({ onSelectContext, children }: { onSelectContext: OnSelectContextCallback; children: React.ReactNode }) => {
        const pendingProjectContextSelection = useAtomValue(pendingProjectContextSelectionAtom);
        const setPendingProjectContextSelection = useSetAtom(pendingProjectContextSelectionAtom);
        const { data: projectEnvironmentClassEntries } = useListProjectEnvironmentClassEntries({
            projectId: pendingProjectContextSelection?.preferredProject.id,
            enabled: !!pendingProjectContextSelection?.preferredProject.id,
        });

        useEffect(() => {
            if (
                projectEnvironmentClassEntries &&
                pendingProjectContextSelection &&
                projectEnvironmentClassEntries.projectId === pendingProjectContextSelection.preferredProject.id
            ) {
                onSelectContext(
                    [
                        ...pendingProjectContextSelection.contexts,
                        {
                            kind: "project",
                            project: pendingProjectContextSelection.preferredProject,
                            environmentClasses: projectEnvironmentClassEntries.environmentClasses,
                        },
                    ],
                    pendingProjectContextSelection.associatedProjects,
                );
                setPendingProjectContextSelection(null);
            }
        }, [
            projectEnvironmentClassEntries,
            onSelectContext,
            setPendingProjectContextSelection,
            pendingProjectContextSelection,
        ]);

        return children;
    },
);
ProjectEnvironmentClassQueryContainer.displayName = "ProjectEnvironmentClassQueryContainer";

const RunnerAuthenticationModal = ({
    onClose,
    onAuthenticationSuccess,
}: {
    onClose: () => void;
    onAuthenticationSuccess: (runnerId: string) => void;
}) => {
    const pendingContextUrl = useAtomValue(pendingContextUrlAtom);
    const { environmentClassEntries } = useEnvironmentClassEntries();
    const runnerId = environmentClassEntries[0]?.runner?.runnerId;
    const { data: authCheckResponse } = useIsUserAuthenticatedWithRunner(runnerId, pendingContextUrl, {
        refetchUntilAuthenticated: true,
        enabled: !!pendingContextUrl && !!runnerId,
    });

    useEffect(() => {
        if (authCheckResponse?.type === "Authenticated" && runnerId) {
            onAuthenticationSuccess(runnerId);
        }
    }, [authCheckResponse, onAuthenticationSuccess, runnerId]);

    if (!runnerId || !pendingContextUrl || authCheckResponse?.type !== "AuthenticationRequired") {
        return null;
    }

    return (
        <SCMAuthenticationModal
            data-track-location={TrackLocations.AIPromptSCMAuthenticationModal}
            repoURL={pendingContextUrl}
            authenticationUrl={authCheckResponse.url}
            scmId={authCheckResponse.scmId}
            scmName={authCheckResponse.scmName}
            runnerId={runnerId}
            onClose={onClose}
            onClickContinue={() => {
                onAuthenticationSuccess(runnerId);
            }}
        />
    );
};

export default UrlSelectorMenu;
